import { useEffect, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import { displayValues, groupByValues } from '@/metrics/constants'

import { orderByValues } from './constants'
import { getDefaultDisplayByTimeRange } from './helpers'

const useFilters = () => {
  const [searchParams, setSearchParams] = useSearchParams()

  const setFiltersInLocalStorage = filters => {
    localStorage.setItem('metricsFiltersParams', JSON.stringify(filters))
  }

  const getFiltersFromLocalStorage = () => {
    return JSON.parse(localStorage.getItem('metricsFiltersParams'))
  }

  const getInitialCommonFilters = () => {
    const filters = getFiltersFromLocalStorage()

    const result = {
      timeRange: searchParams.get('timeRange') || filters?.timeRange || 'month',
      regionId: searchParams.get('regionId') || filters?.regionId || 'all',
    }

    if (filters?.timeRange === 'custom') {
      const startDate = searchParams.get('startDate')
      const endDate = searchParams.get('endDate')

      result.startDate = startDate || filters?.startDate
      result.endDate = endDate || filters?.endDate
    }

    return result
  }

  const getInitialProductsFilters = () => {
    const filters = getFiltersFromLocalStorage()

    return {
      groupBy: searchParams.get('productsGroupBy') || filters?.productsGroupBy || groupByValues.products,
      ordering: searchParams.get('productsOrdering') || filters?.productsOrdering || `-${orderByValues.ratio}`,
      query: searchParams.get('productsQuery') || filters?.productsQuery || '',
    }
  }

  const getInitialRefundMethodsFilters = () => {
    const filters = getFiltersFromLocalStorage()

    return {
      ordering:
        searchParams.get('refundMethodsOrdering') || filters?.refundMethodsOrdering || `-${orderByValues.ratio}`,
    }
  }

  const getInitialTopCountriesFilters = () => {
    const filters = getFiltersFromLocalStorage()

    return {
      ordering: searchParams.get('topCountriesOrdering') || filters?.topCountriesOrdering || `-${orderByValues.ratio}`,
    }
  }

  const getInitialStoresFilters = () => {
    const filters = getFiltersFromLocalStorage()

    return {
      ordering: searchParams.get('storesOrdering') || filters?.storesOrdering || `-${orderByValues.ratio}`,
    }
  }

  const getInitialReasonsFilters = () => {
    const filters = getFiltersFromLocalStorage()

    return {
      ordering: searchParams.get('reasonsOrdering') || filters?.reasonsOrdering || `-${orderByValues.count}`,
    }
  }

  const getInitialReturnSalesRatioFilters = () => {
    const filters = getFiltersFromLocalStorage()

    return {
      timeDisplayReturnSales:
        searchParams.get('timeDisplayReturnSales') || filters?.timeDisplayReturnSales || displayValues.daily,
    }
  }

  const getInitialReturnsAndExchangesRatioFilters = () => {
    const filters = getFiltersFromLocalStorage()

    return {
      timeDisplayReturnsExchanges:
        searchParams.get('timeDisplayReturnsExchanges') || filters?.timeDisplayReturnsExchanges || displayValues.daily,
    }
  }

  const [commonFilters, setCommonFilters] = useState(getInitialCommonFilters())
  const [productsFilters, setProductsFilters] = useState(getInitialProductsFilters())
  const [refundMethodsFilters, setRefundMethodsFilters] = useState(getInitialRefundMethodsFilters())
  const [topCountriesFilters, setTopCountriesFilters] = useState(getInitialTopCountriesFilters())
  const [storesFilters, setStoresFilters] = useState(getInitialStoresFilters())
  const [reasonsFilters, setReasonsFilters] = useState(getInitialReasonsFilters())
  const [returnSalesRatioFilters, setReturnSalesRatioFilters] = useState(getInitialReturnSalesRatioFilters())
  const [returnsAndExchangesRatioFilters, setReturnsAndExchangesRatioFilters] = useState(
    getInitialReturnsAndExchangesRatioFilters()
  )

  const updateCommonFilters = newFilters => {
    const defaultDisplay = getDefaultDisplayByTimeRange(newFilters.timeRange, newFilters.startDate, newFilters.endDate)

    setCommonFilters(newFilters)
    setReturnSalesRatioFilters({ timeDisplayReturnSales: defaultDisplay })
    setReturnsAndExchangesRatioFilters({ timeDisplayReturnsExchanges: defaultDisplay })
  }

  useEffect(() => {
    const currentParams = {
      timeRange: searchParams.get('timeRange'),
      regionId: searchParams.get('regionId'),
      returnSalesRatioFilters: {
        timeDisplayReturnSales: searchParams.get('timeDisplayReturnSales'),
      },
      returnsAndExchangesRatioFilters: {
        timeDisplayReturnsExchanges: searchParams.get('timeDisplayReturnsExchanges'),
      },
      productsFilters: {
        groupBy: searchParams.get('productsGroupBy'),
        ordering: searchParams.get('productsOrdering'),
        query: searchParams.get('productsQuery') || '',
      },
      refundMethodsFilters: {
        ordering: searchParams.get('refundMethodsOrdering'),
      },
      topCountriesFilters: {
        ordering: searchParams.get('topCountriesOrdering'),
      },
      storesFilters: {
        ordering: searchParams.get('storesOrdering'),
      },
      reasonsFilters: {
        ordering: searchParams.get('reasonsOrdering'),
      },
    }

    if (currentParams.timeRange === 'custom') {
      currentParams.startDate = searchParams.get('startDate')
      currentParams.endDate = searchParams.get('endDate')
    }

    if (
      JSON.stringify(currentParams) !==
      JSON.stringify({
        ...commonFilters,
        productsFilters: { ...productsFilters },
        refundMethodsFilters: { ...refundMethodsFilters },
        topCountriesFilters: { ...topCountriesFilters },
        storesFilters: { ...storesFilters },
        reasonsFilters: { ...reasonsFilters },
        returnSalesRatioFilters: { ...returnSalesRatioFilters },
        returnsAndExchangesRatioFilters: { ...returnsAndExchangesRatioFilters },
      })
    ) {
      const params = {
        timeRange: commonFilters.timeRange,
        regionId: commonFilters.regionId,
        timeDisplayReturnSales: returnSalesRatioFilters.timeDisplayReturnSales,
        timeDisplayReturnsExchanges: returnsAndExchangesRatioFilters.timeDisplayReturnsExchanges,
        productsGroupBy: productsFilters.groupBy,
        productsOrdering: productsFilters.ordering,
        productsQuery: productsFilters.query,
        refundMethodsOrdering: refundMethodsFilters.ordering,
        topCountriesOrdering: topCountriesFilters.ordering,
        storesOrdering: storesFilters.ordering,
        reasonsOrdering: reasonsFilters.ordering,
      }

      if (commonFilters.timeRange === 'custom') {
        params.startDate = commonFilters.startDate
        params.endDate = commonFilters.endDate
      }

      setSearchParams(params)
      setFiltersInLocalStorage(params)
    }
  }, [
    commonFilters,
    searchParams,
    setSearchParams,
    productsFilters,
    refundMethodsFilters,
    topCountriesFilters,
    storesFilters,
    reasonsFilters,
    returnSalesRatioFilters,
    returnsAndExchangesRatioFilters,
  ])

  const getUrlSearchParams = () => {
    return new URLSearchParams(window.location.search)?.toString()
  }

  return {
    commonFilters,
    setCommonFilters: updateCommonFilters,
    getUrlSearchParams,
    productsFilters,
    setProductsFilters,
    refundMethodsFilters,
    setRefundMethodsFilters,
    topCountriesFilters,
    setTopCountriesFilters,
    storesFilters,
    setStoresFilters,
    reasonsFilters,
    setReasonsFilters,
    returnSalesRatioFilters,
    setReturnSalesRatioFilters,
    returnsAndExchangesRatioFilters,
    setReturnsAndExchangesRatioFilters,
  }
}

export default useFilters
